// src/oura/components/newscan/RadioSeg.tsx
//
// A pick-one segmented track: how customers buy (Visit us / Online / Both /
// We go to them), Auto / On / Off, Keep / Take. It looks like the kit's Seg
// but is a real radio group (useRadioGroup): one tab stop, arrow keys and
// Home / End move and choose, wrapping. Seg's aria-pressed buttons read as
// toggles, which says any number can be on.
//
// `suggested` marks the option the read proposed with a small green dot and
// the word "Suggested" for screen readers.

import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@oura/lib/cn";
import { useRadioGroup } from "@oura/lib/useRadioGroup";
import { TYPE } from "@oura/ui";
import { RADIO_SEG } from "@oura/newscan/copy";

export interface RadioSegOption<K extends string> {
    key: K;
    label: ReactNode;
    icon?: LucideIcon;
    /** The read's proposal: a green dot, and "Suggested" for screen readers. */
    suggested?: boolean;
}

export interface RadioSegProps<K extends string> {
    options: RadioSegOption<K>[];
    /** Null: nothing chosen yet (the first option takes the tab stop). */
    value: K | null;
    onChange(key: K): void;
    /** The group's name. Give this or `labelledBy`. */
    ariaLabel?: string;
    /** id of a visible label (a `dt`, a Field label). */
    labelledBy?: string;
    describedBy?: string;
    /** "sm" for a Keep / Take per row; "md" (30px) otherwise. */
    size?: "md" | "sm";
    /** Under 640px: a 2-column grid that fills the row, for four long options. */
    wrapOnPhone?: boolean;
    disabled?: boolean;
    className?: string;
}

// Inset ring: the track clips an outline drawn outside the item.
const ITEM_FOCUS = "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-600";

export function RadioSeg<K extends string>({
    options,
    value,
    onChange,
    ariaLabel,
    labelledBy,
    describedBy,
    size = "md",
    wrapOnPhone,
    disabled,
    className,
}: RadioSegProps<K>) {
    const keys = options.map((o) => o.key);
    const radio = useRadioGroup<K>(keys, value ?? undefined, (k) => {
        if (!disabled) onChange(k);
    });
    return (
        <div
            role="radiogroup"
            aria-label={labelledBy ? undefined : ariaLabel}
            aria-labelledby={labelledBy}
            aria-describedby={describedBy}
            aria-disabled={disabled || undefined}
            className={cn(
                "inline-flex max-w-full gap-0.5 rounded-[10px] bg-gray-100 p-0.5",
                wrapOnPhone && "max-sm:grid max-sm:w-full max-sm:grid-cols-2",
                disabled && "opacity-60",
                className,
            )}
        >
            {options.map((o, i) => {
                const on = o.key === value;
                const Icon = o.icon;
                return (
                    <button
                        key={o.key}
                        type="button"
                        {...radio(i)}
                        disabled={disabled}
                        className={cn(
                            "relative inline-flex min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg font-medium",
                            // 44px target round a 30px item, taller not wider.
                            "after:absolute after:inset-x-0 after:-inset-y-[7px] after:content-['']",
                            "motion-safe:transition-colors disabled:cursor-not-allowed",
                            size === "sm" ? cn("h-[26px] px-2.5", TYPE.tm) : cn("h-[30px] px-3", TYPE.tc),
                            on
                                ? "bg-white text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.04)]"
                                : "text-gray-500 hover:text-gray-900",
                            ITEM_FOCUS,
                        )}
                    >
                        {Icon && <Icon aria-hidden size={14} className="shrink-0" />}
                        <span className="truncate">{o.label}</span>
                        {o.suggested && (
                            <>
                                <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                                <span className="sr-only">, {RADIO_SEG.suggested}</span>
                            </>
                        )}
                    </button>
                );
            })}
        </div>
    );
}
